import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { PageHeader } from "@bednbody/ui/components/page-header";
import {
  PulseIcon,
  ClockIcon,
  ArrowClockwiseIcon,
  UserCheckIcon,
  UserMinusIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Input } from "@bednbody/ui/components/input";
import { sessionStateLabel, therapistOutcomeLabel } from "@bednbody/logic";
import { useAuth } from "@/lib/auth";
import { isHubScoped } from "@/lib/jwt-claims";
import { useAttendance } from "../ops-dashboard/ops-dashboard.hooks";
import { StatCard } from "../ops-dashboard/components/StatCard";

import { SessionDetailModal, type AttendanceRow } from "./SessionDetailModal";
import { OUTCOME_VARIANT, STATE_VARIANT } from "./attendance-tone";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Attendance, on the Session model.
 *
 * ⚠ THE PAGE SHOWS TWO AXES AND MUST KEEP THEM APART. `10 Therapist End/
 * Therapist Session Daily Operations` files Late Start under ATTENDANCE
 * OUTCOME, which is explicitly a different axis from the Session state and is
 * *recorded, never sat in*. So the Status column carries the state and the
 * badge beside it carries the outcome — a single merged chip reading "Late"
 * would invent a state the approved machine does not have.
 *
 * The recorded outcome only exists after the nightly settle. Inside the window
 * the badge shows `Running late` instead, which is a prompt about someone who
 * can still act, not a record of someone who did not.
 */
export function AttendancePage() {
  const { claims } = useAuth();
  const queryClient = useQueryClient();
  const hubScoped = claims ? isHubScoped(claims.role) : false;
  const hubId = hubScoped ? (claims?.hub_id ?? null) : null;

  const [date, setDate] = useState(todayStr());
  const [selected, setSelected] = useState<AttendanceRow | null>(null);
  const isToday = date === todayStr();

  const { data: attendance, isLoading } = useAttendance(hubId, date);

  const rows = attendance ?? [];
  // Holding a Session for the date at all — the denominator that means
  // something. A hub's whole roster is not "expected today": a therapist on a
  // finalized Off date is not absent, she is off.
  const scheduled = rows.filter((r) => r.attendance !== null && r.attendance.state !== "off");
  const onDuty = scheduled.filter((r) => r.attendance?.onDuty);
  const onBooking = onDuty.filter((r) => r.currentBooking);
  const notStarted = scheduled.filter((r) => r.attendance?.state === "not_started");
  const runningLate = scheduled.filter((r) => r.attendance?.runningLate);
  const lateStarts = scheduled.filter((r) => r.attendance?.lateStart);
  const noShows = scheduled.filter((r) => r.attendance?.outcome === "no_show");

  const columns: ColumnDef<AttendanceRow, unknown>[] = [
    {
      id: "name",
      accessorFn: (person) => person.name,
      header: "Staff member",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.name}
          subtitle={row.original.role.replaceAll("_", " ")}
          fallback={row.original.name.slice(0, 2).toUpperCase()}
        />
      ),
    },
    {
      id: "role",
      accessorFn: (person) => person.role,
      header: "Role",
      cell: ({ row }) => <span className="capitalize">{row.original.role}</span>,
    },
    ...(!hubScoped
      ? [{ id: "hub", accessorFn: (person: AttendanceRow) => person.hubName ?? "", header: "Hub" }]
      : []),
    {
      id: "session",
      accessorFn: (person) => person.startsAt ?? "",
      header: "Session",
      cell: ({ row }) => <SessionWindow row={row.original} />,
    },
    {
      id: "started",
      accessorFn: (person) => person.startedTime ?? "",
      header: "Started",
      cell: ({ row }) => row.original.startedTime ?? "—",
    },
    {
      id: "ended",
      accessorFn: (person) => person.endedTime ?? "",
      header: "Ended",
      cell: ({ row }) => row.original.endedTime ?? "—",
    },
    {
      id: "booking",
      accessorFn: (person) => person.currentBooking ?? "",
      header: "Current booking",
      cell: ({ row }) => row.original.currentBooking ?? "—",
    },
    {
      id: "status",
      accessorFn: (person) => person.attendance?.state ?? "none",
      header: "Status",
      cell: ({ row }) => <StatusCell row={row.original} />,
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.name}
          actions={[{ label: "View session", onSelect: () => setSelected(row.original) }]}
        />
      ),
    },
  ];

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        actions={
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="Service date"
              value={date}
              max={todayStr()}
              onChange={(e) => (e.target.value ? setDate(e.target.value) : null)}
              className="w-auto"
            />
            <Button
              variant="outline"
              onClick={() => queryClient.invalidateQueries({ queryKey: ["attendance"] })}
            >
              <ArrowClockwiseIcon weight="fill" />
              Refresh
            </Button>
          </div>
        }
        description={
          <>
            Who's on session and what they're working on.
            {hubScoped ? " Limited to your assigned hub." : null}
          </>
        }
        title="Attendance"
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {isToday ? (
          <>
            <StatCard
              label="On session"
              value={`${onDuty.length}/${scheduled.length}`}
              icon={UserCheckIcon}
              hint="started, inside the window"
            />
            <StatCard label="On a booking" value={onBooking.length} icon={PulseIcon} />
            <StatCard
              label="Not started"
              value={notStarted.length}
              icon={UserMinusIcon}
              tone={notStarted.length > 0 ? "warning" : "default"}
            />
            <StatCard
              label="Running late"
              value={runningLate.length}
              icon={WarningIcon}
              hint="past their start, not started"
              tone={runningLate.length > 0 ? "warning" : "default"}
            />
          </>
        ) : (
          <>
            <StatCard
              label="Started"
              value={`${scheduled.length - noShows.length}/${scheduled.length}`}
              icon={UserCheckIcon}
            />
            <StatCard
              label="Late starts"
              value={lateStarts.length}
              icon={ClockIcon}
              tone={lateStarts.length > 0 ? "warning" : "default"}
            />
            <StatCard
              label="No-shows"
              value={noShows.length}
              icon={UserMinusIcon}
              tone={noShows.length > 0 ? "warning" : "default"}
            />
            <StatCard
              label="Not scheduled"
              value={rows.length - scheduled.length}
              icon={UserMinusIcon}
            />
          </>
        )}
      </div>

      <h2 className="type-title-subsection">
        {isToday ? "Today's sessions" : `Sessions · ${date}`}
      </h2>

      <AdminDataTable
        presentation="standalone"
        data={rows}
        columns={columns}
        getRowId={(person) => person.id}
        searchText={(person) =>
          [
            person.name,
            person.role,
            person.hubName,
            person.currentBooking,
            person.sessionCode,
          ].join(" ")
        }
        searchPlaceholder="Search staff, hub, or booking…"
        facets={[
          {
            id: "role",
            label: "Role",
            options: [
              { value: "therapist", label: "Therapists" },
              { value: "rider", label: "Riders" },
            ],
            getValue: (person) => person.role,
          },
        ]}
        initialSorting={[{ id: "name", desc: false }]}
        loading={isLoading}
        exportFilename={`attendance-${date}`}
        exportColumns={[
          { header: "Name", value: (person) => person.name },
          { header: "Role", value: (person) => person.role },
          { header: "Hub", value: (person) => person.hubName },
          { header: "Started", value: (person) => person.startedTime },
          { header: "Ended", value: (person) => person.endedTime },
          { header: "Booking", value: (person) => person.currentBooking },
          { header: "State", value: (person) => person.attendance?.state },
        ]}
      />

      <SessionDetailModal staff={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

/** The scheduled window, prefixed with the rider's Session code where there is one. */
function SessionWindow({ row }: { row: AttendanceRow }) {
  if (!row.startsAt || !row.endsAt) return <span>-</span>;
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });
  return (
    <span>
      {row.sessionCode ? `${row.sessionCode} · ` : ""}
      {fmt(row.startsAt)}–{fmt(row.endsAt)}
    </span>
  );
}

/**
 * State chip, plus the attendance badge when there is one to show.
 *
 * The order of the three badge cases is the rule: a RECORDED outcome outranks
 * everything, then the recorded Late Start on a session still running, then the
 * live prompt. Showing "Running late" beside a settled No-Show would be
 * advising someone to act on a day that is over.
 */
function StatusCell({ row }: { row: AttendanceRow }) {
  const a = row.attendance;
  if (!a) {
    return (
      <Badge variant="muted" className="whitespace-nowrap">
        Not scheduled
      </Badge>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={STATE_VARIANT[a.state]} className="whitespace-nowrap">
        {sessionStateLabel(a.state)}
      </Badge>
      {a.outcome ? (
        <Badge variant={OUTCOME_VARIANT[a.outcome]} className="whitespace-nowrap">
          {therapistOutcomeLabel(a.outcome)}
          {/* An outcome this page derived rather than read is marked, so a
              hub manager is never shown a preview as though it were the
              settled record. */}
          {a.outcomeIsRecorded ? "" : " (pending)"}
        </Badge>
      ) : a.lateStart ? (
        <Badge variant="warning" className="whitespace-nowrap">
          Late start
        </Badge>
      ) : a.runningLate ? (
        <Badge variant="warning" className="whitespace-nowrap">
          Running late
        </Badge>
      ) : null}
    </div>
  );
}
